import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  fetchPaymentSetting,
  fetchReservationSetting,
  fetchStripeStatus,
  savePaymentSetting,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import {
  calcPriceIncludingTax,
  DEFAULT_TAX_RATE_PERCENT,
  PaymentSetting,
  PaymentType,
} from '../../../types/reservation';
import NumberField from '../../../Components/NumberInput/NumberField';
import {
  SavedSnackbar,
  TabDescription,
  UnsavedChangesBar,
  useDirtyNotifier,
  useSaveFeedback,
} from './SettingsShared';

interface Props {
  storeId: string;
  /** dirty（サーバ値と差分あり）の変化を親へ通知する（タブ切替時の確認用） */
  onDirtyChange?: (dirty: boolean) => void;
}

/** 未設定店舗（GET が 404 → null）に表示するデフォルト値 */
const defaultSetting: PaymentSetting = {
  paymentEnabled: false,
  paymentRequired: false,
  paymentType: 'DEPOSIT_FIXED',
  depositAmount: null,
  tiers: [],
  // 既定は店舗が手数料を負担（満額返金）。BE の未指定既定と揃える。
  refundFeePolicy: 'STORE_ABSORBS',
};

/**
 * タブ⑦決済方針。事前決済（デポジット/前払い）の方針とキャンセルポリシーを設定する。
 * 未保存の変更がある間は画面下部に sticky 保存バーを表示する（フィードバック3）。
 */
const PaymentPolicyTab: React.FC<Props> = ({ storeId, onDirtyChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const paymentSetting = useSelector(
    (state: RootState) => state.reservationSettings.paymentSetting,
  );
  const stripeStatus = useSelector((state: RootState) => state.reservationSettings.stripeStatus);
  // デポジットの税込プレビュー算出用の税率（受付設定から取得。未設定は既定10%）
  const taxRatePercent = useSelector(
    (state: RootState) =>
      state.reservationSettings.setting?.taxRatePercent ?? DEFAULT_TAX_RATE_PERCENT,
  );

  const [form, setForm] = useState<PaymentSetting | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    // Stripe 連携ステータスの取得失敗は致命的でないため警告表示に留めず無視する
    dispatch(fetchStripeStatus(storeId));
    // デポジットの税込プレビューに使う税率を受付設定から取得する
    dispatch(fetchReservationSetting(storeId));
    dispatch(fetchPaymentSetting(storeId))
      .unwrap()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [dispatch, storeId, showError]);

  // null（未設定）ならデフォルト値を基準にする（dirty 判定・破棄に使う）
  const baseline = paymentSetting ?? defaultSetting;

  useEffect(() => {
    if (loaded) {
      setForm(baseline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, paymentSetting]);

  const dirty = form !== null && JSON.stringify(form) !== JSON.stringify(baseline);
  useDirtyNotifier(dirty, onDirtyChange);

  if (!loaded || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const updateTier = (index: number, key: 'hoursBeforeStart' | 'refundPercent', value: number) => {
    setForm({
      ...form,
      tiers: form.tiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)),
    });
  };

  // 返金tier例示の基準額（税込）。DEPOSIT_FIXED でデポジット額が入力済みのときだけ実額換算する。
  // COURSE_FULL / DEPOSIT_PER_PERSON など予約時に額が決まる場合は null（割合のみ表示）。
  const refundBaseIncludingTax =
    form.paymentType === 'DEPOSIT_FIXED' && form.depositAmount != null
      ? calcPriceIncludingTax(form.depositAmount, taxRatePercent)
      : null;

  // 顧客都合キャンセル時の手数料負担ポリシー。未指定（旧BE）は STORE_ABSORBS 扱い。
  const refundFeePolicy = form.refundFeePolicy ?? 'STORE_ABSORBS';

  const save = () => {
    if (form.paymentType !== 'COURSE_FULL' && form.paymentEnabled && form.depositAmount == null) {
      setError('デポジット金額を入力してください。');
      return;
    }
    if (form.tiers.some((t) => t.refundPercent < 0 || t.refundPercent > 100)) {
      setError('返金率は 0〜100% の範囲で入力してください。');
      return;
    }
    const payload: PaymentSetting = {
      ...form,
      // コース全額の場合はデポジット金額を使わない
      depositAmount: form.paymentType === 'COURSE_FULL' ? null : form.depositAmount,
    };
    setSaving(true);
    dispatch(savePaymentSetting({ storeId, setting: payload }))
      .unwrap()
      .then(() => showSaved())
      .catch(showError)
      .finally(() => setSaving(false));
  };

  return (
    <Box>
      <TabDescription text="予約時の事前決済（デポジット/前払い）とキャンセルポリシーを設定します" />

      {stripeStatus && !stripeStatus.connected && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/${storeId}/manage/store`)}
            >
              店舗管理へ
            </Button>
          }
        >
          事前決済の利用にはStripe連携が必要です。店舗管理画面から連携してください
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          事前決済の方針
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.paymentEnabled}
              onChange={(e) => setForm({ ...form, paymentEnabled: e.target.checked })}
            />
          }
          label="事前決済を受け付ける"
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.paymentRequired}
              onChange={(e) => setForm({ ...form, paymentRequired: e.target.checked })}
            />
          }
          label="事前決済を必須にする"
        />
        <TextField
          select
          label="決済タイプ"
          value={form.paymentType}
          onChange={(e) => setForm({ ...form, paymentType: e.target.value as PaymentType })}
          fullWidth
          margin="dense"
          size="small"
        >
          <MenuItem value="DEPOSIT_FIXED">固定デポジット</MenuItem>
          <MenuItem value="DEPOSIT_PER_PERSON">1名あたりデポジット</MenuItem>
          <MenuItem value="COURSE_FULL">コース全額</MenuItem>
        </TextField>
        <NumberField
          label="デポジット金額（税抜・円）"
          value={form.depositAmount}
          // 空/0 は「未設定（null）」として扱う（0円デポジットは意味を持たないため）
          onChangeValue={(v) => setForm({ ...form, depositAmount: v === 0 ? null : v })}
          min={0}
          disabled={form.paymentType === 'COURSE_FULL'}
          helperText={
            form.paymentType === 'COURSE_FULL'
              ? 'コース全額の場合、デポジット金額は不要です'
              : form.depositAmount != null
                ? `税込 ¥${calcPriceIncludingTax(
                    form.depositAmount,
                    taxRatePercent,
                  ).toLocaleString()}（税率${taxRatePercent}%）`
                : `税抜で入力してください（お客様には税込表示・税率${taxRatePercent}%）`
          }
          fullWidth
          margin="dense"
          size="small"
        />
        {/* コース全額の決済スキップ条件（feast-be ADR 0103 §6） */}
        {form.paymentType === 'COURSE_FULL' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            コース金額が¥100未満の場合、および受付設定でコース選択を必須にしておらず顧客がコース未選択で予約した場合は、事前決済（デポジット）を取得しません。
          </Alert>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          キャンセルポリシー（返金段階）
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          予約時刻の何時間前までにキャンセルした場合に何%返金するかを段階で設定します。
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          返金率は決済額（税込）に対する割合です。例）税込1,000円の予約で「48時間前まで100%」なら1,000円、「24時間前まで50%」なら500円を返金します。
        </Typography>
        {form.tiers.map((tier, index) => (
          <Box key={index} sx={{ py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NumberField
                label="時間前まで"
                value={tier.hoursBeforeStart}
                onChangeValue={(v) => updateTier(index, 'hoursBeforeStart', v)}
                min={0}
                size="small"
              />
              <Typography variant="body2">→</Typography>
              <NumberField
                label="返金率（%）"
                value={tier.refundPercent}
                onChangeValue={(v) => updateTier(index, 'refundPercent', v)}
                min={0}
                max={100}
                size="small"
              />
              <IconButton
                size="small"
                onClick={() =>
                  setForm({ ...form, tiers: form.tiers.filter((_, i) => i !== index) })
                }
                aria-label="返金段階を削除"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* デポジット税込額が算出できるときは実額の返金例を、算出できないときは割合のみを表示 */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5 }}>
              {refundBaseIncludingTax != null
                ? `${tier.hoursBeforeStart}時間前まで ${tier.refundPercent}% → ¥${Math.floor(
                    (refundBaseIncludingTax * tier.refundPercent) / 100,
                  ).toLocaleString()} 返金`
                : `${tier.hoursBeforeStart}時間前まで ${tier.refundPercent}% 返金`}
            </Typography>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            setForm({ ...form, tiers: [...form.tiers, { hoursBeforeStart: 24, refundPercent: 100 }] })
          }
        >
          返金段階を追加
        </Button>

        {/* 顧客都合キャンセル時の手数料負担ポリシー（feast-be ADR 0103） */}
        <TextField
          select
          label="キャンセル手数料の負担"
          value={refundFeePolicy}
          onChange={(e) =>
            setForm({
              ...form,
              refundFeePolicy: e.target.value as PaymentSetting['refundFeePolicy'],
            })
          }
          fullWidth
          margin="dense"
          size="small"
          sx={{ mt: 1 }}
        >
          <MenuItem value="STORE_ABSORBS">店舗が負担（満額返金）</MenuItem>
          <MenuItem value="CUSTOMER_BEARS">顧客が負担（手数料分を差し引いて返金）</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          顧客都合のキャンセルのみに適用されます。店舗都合のキャンセルは常に満額返金です。
        </Typography>

        {/* 決済手数料の注記（feast-be ADR 0103 論点2。率は明記しない） */}
        <Alert severity="info" sx={{ mt: 2 }}>
          FEASTは決済手数料を受け取りません。ただしStripe所定の決済手数料が別途発生します。
          <Link
            href="https://stripe.com/jp/pricing"
            target="_blank"
            rel="noopener"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Stripeの料金はこちら
          </Link>
        </Alert>
      </Paper>

      {/* 未保存の変更がある間だけ表示される sticky 保存バー（保存し忘れ防止） */}
      <UnsavedChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={() => {
          setForm(baseline);
          setError(null);
        }}
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default PaymentPolicyTab;
